export type SkillLevel = 'starter' | 'recreatief' | 'intermediate' | 'advanced' | 'club';
export type ProductCategory =
  | 'startersets'
  | 'paddles'
  | 'ballen'
  | 'netten-en-belijning'
  | 'schoenen-en-bescherming'
  | 'tassen-accessoires'
  | 'clubpakketten';
export type SupplierStatus =
  | 'verified_dealer_needed'
  | 'distribution_to_verify'
  | 'fallback_retail_source'
  | 'private_label_later';
export type CommercialStatus = 'lead_capture' | 'quote_request' | 'supplier_verification';
export type StockStatus = 'not_stocked_prelaunch' | 'in_stock';

export interface Product {
  sku: string;
  slug: string;
  name: string;
  category: ProductCategory;
  level: SkillLevel;
  brand: string;
  supplierTarget: string;
  supplierStatus: SupplierStatus;
  supplierPathDocumented: boolean;
  commercialStatus: CommercialStatus;
  stockStatus: StockStatus;
  checkoutEnabled: boolean;
  priceInclVatTarget: number;
  marginTargetPct: number;
  shortDescription: string;
  whyStock: string;
  badges: string[];
  complianceNotes: string[];
}

export const products: Product[] = [
  {
    sku: 'PPB-START-SET-2P-001',
    slug: 'pickleball-startset-2-spelers',
    name: 'ProPickleball Startset 2 spelers',
    category: 'startersets',
    level: 'starter',
    brand: 'ProPickleball curated',
    supplierTarget: 'EU starterset-distributeur of private label later',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 79,
    marginTargetPct: 35,
    shortDescription: 'Instapset met 2 paddles, outdoorballen en draagtas voor thuis, clubintro of vakantiepark.',
    whyStock: 'Beste eerste aankoop voor nieuwe spelers en geschikt voor de zoekterm pickleball set kopen.',
    badges: ['Starterset', '2 spelers', 'NL/BE keuzehulp'],
    complianceNotes: ['Controleer productveiligheid en indoor/outdoor uitleg.', 'Geen toernooi-claim zonder bewijs.']
  },
  {
    sku: 'PPB-START-SET-4P-001',
    slug: 'pickleball-startset-4-spelers',
    name: 'ProPickleball Startset 4 spelers',
    category: 'startersets',
    level: 'starter',
    brand: 'ProPickleball curated',
    supplierTarget: 'EU starterset-distributeur',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 139,
    marginTargetPct: 34,
    shortDescription: 'Gezins- en clubintroset voor vier spelers met focus op makkelijk starten.',
    whyStock: 'Sterk voor scholen, gezinnen en recreatieparken waar meerdere spelers tegelijk starten.',
    badges: ['4 spelers', 'School/club', 'Bundel'],
    complianceNotes: ['Controleer verpakking en waarschuwingen.', 'Levertermijn pas tonen na supplierbevestiging.']
  },
  {
    sku: 'PPB-JOOLA-JOURNEY-001',
    slug: 'joola-journey-pickleball-paddle',
    name: 'JOOLA Journey Pickleball Paddle',
    category: 'paddles',
    level: 'starter',
    brand: 'JOOLA',
    supplierTarget: 'JOOLA Europe / erkende Benelux-sportdistributeur',
    supplierStatus: 'verified_dealer_needed',
    supplierPathDocumented: false,
    commercialStatus: 'supplier_verification',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 49,
    marginTargetPct: 30,
    shortDescription: 'Toegankelijke paddle voor beginners die meer willen dan een houten instapmodel.',
    whyStock: 'Laagdrempelig A-merk en geschikt voor keuzehulp rond beste pickleball paddle voor beginners.',
    badges: ['Beginner', 'A-merk', 'Paddle'],
    complianceNotes: ['Controleer actuele modelbeschikbaarheid en dealerprijzen.', 'Gebruik merkbeelden alleen na toestemming.']
  },
  {
    sku: 'PPB-FRANKLIN-JET-SET-001',
    slug: 'franklin-jet-starter-set',
    name: 'Franklin Jet Starter Set',
    category: 'startersets',
    level: 'starter',
    brand: 'Franklin Sports',
    supplierTarget: 'Franklin Sports / EU-distributeur',
    supplierStatus: 'verified_dealer_needed',
    supplierPathDocumented: false,
    commercialStatus: 'supplier_verification',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 59,
    marginTargetPct: 30,
    shortDescription: 'Betaalbare merkset voor recreatieve spelers en cadeau-aankopen.',
    whyStock: 'Logische bundelkans met outdoorballen voor wie direct met twee spelers wil starten.',
    badges: ['Starter', 'Merkset', 'Bundelbaar'],
    complianceNotes: ['Controleer EU-labeling.', 'Geen officiele competitieclaim zonder bewijs.']
  },
  {
    sku: 'PPB-SELKIRK-AMPED-S2-001',
    slug: 'selkirk-amped-control-s2',
    name: 'Selkirk Amped Control S2',
    category: 'paddles',
    level: 'intermediate',
    brand: 'Selkirk',
    supplierTarget: 'Selkirk Sport dealerprogramma / EU-distributeur',
    supplierStatus: 'verified_dealer_needed',
    supplierPathDocumented: false,
    commercialStatus: 'supplier_verification',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 129,
    marginTargetPct: 28,
    shortDescription: 'Controlegerichte paddle voor recreatieve spelers die beter willen plaatsen.',
    whyStock: 'Goede stap voor recreatieve spelers die meer controle willen zonder direct naar het hoogste segment te gaan.',
    badges: ['Controle', 'Intermediate', 'Premium route'],
    complianceNotes: ['Dealerstatus en merkcontent schriftelijk bevestigen.', 'Controleer exacte specs per modelvariant.']
  },
  {
    sku: 'PPB-JOOLA-HYPERION-16-001',
    slug: 'joola-ben-johns-hyperion-16mm',
    name: 'JOOLA Ben Johns Hyperion 16mm',
    category: 'paddles',
    level: 'advanced',
    brand: 'JOOLA',
    supplierTarget: 'JOOLA Europe / erkende distributeur',
    supplierStatus: 'verified_dealer_needed',
    supplierPathDocumented: false,
    commercialStatus: 'supplier_verification',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 199,
    marginTargetPct: 25,
    shortDescription: 'Premium paddle voor gevorderde spelers die meer controle en power zoeken.',
    whyStock: 'Sterke keuze voor spelers die later een serieuzere paddle willen kiezen met goede uitleg vooraf.',
    badges: ['Premium', 'Advanced', 'Dealer nodig'],
    complianceNotes: ['Specs moeten overeenkomen met exacte variant.', 'Geen productfoto of pro-claim zonder toestemming.']
  },
  {
    sku: 'PPB-FRANKLIN-X40-12-001',
    slug: 'franklin-x40-outdoor-pickleball-ballen-12-pack',
    name: 'Franklin X-40 Outdoor Pickleball Ballen 12-pack',
    category: 'ballen',
    level: 'recreatief',
    brand: 'Franklin Sports',
    supplierTarget: 'Franklin Sports / EU-distributeur',
    supplierStatus: 'verified_dealer_needed',
    supplierPathDocumented: false,
    commercialStatus: 'supplier_verification',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 29,
    marginTargetPct: 32,
    shortDescription: 'Outdoorballen voor training, recreatief spel en clubgebruik.',
    whyStock: 'Verbruiksartikel met herhaalaankoop en add-on potentie bij paddles en startersets.',
    badges: ['Outdoor', 'Add-on', 'Herhaalaankoop'],
    complianceNotes: ['Vermeld indoor/outdoor verschil duidelijk.', 'Controleer batchkwaliteit bij ontvangst.']
  },
  {
    sku: 'PPB-INDOOR-BALL-6-001',
    slug: 'indoor-pickleball-ballen-6-pack',
    name: 'Indoor Pickleball Ballen 6-pack',
    category: 'ballen',
    level: 'recreatief',
    brand: 'Curated brand',
    supplierTarget: 'EU-sportdistributeur',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 16,
    marginTargetPct: 35,
    shortDescription: 'Zachtere indoorballen voor sporthallen, scholen en verenigingen.',
    whyStock: 'NL/BE speelt vaak indoor; duidelijke uitleg voorkomt verkeerde aankoop en retouren.',
    badges: ['Indoor', 'Sporthal', 'Budget'],
    complianceNotes: ['Maak verschil met outdoorballen visueel duidelijk.', 'Controleer gewicht en diameter via leverancierdata.']
  },
  {
    sku: 'PPB-PORTABLE-NET-22FT-001',
    slug: 'draagbaar-pickleball-net-22ft',
    name: 'Draagbaar Pickleball Net 22 ft',
    category: 'netten-en-belijning',
    level: 'club',
    brand: 'ProPickleball curated',
    supplierTarget: 'EU-nettenleverancier / sportinrichting groothandel',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 149,
    marginTargetPct: 35,
    shortDescription: 'Draagbaar net voor oprit, sporthal, school, clubtraining en vakantiepark.',
    whyStock: 'Hoge orderwaarde en B2B-kans bij clubs, scholen en recreatieparken.',
    badges: ['Club', 'Draagbaar', 'B2B'],
    complianceNotes: ['Controleer netbreedte, hoogte en stabiliteit.', 'Retourkosten voor bulky shipping vooraf bepalen.']
  },
  {
    sku: 'PPB-COURT-LINES-001',
    slug: 'pickleball-belijningskit',
    name: 'Pickleball Belijningskit',
    category: 'netten-en-belijning',
    level: 'starter',
    brand: 'ProPickleball curated',
    supplierTarget: 'EU-sportaccessoire-distributeur',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 29,
    marginTargetPct: 45,
    shortDescription: 'Tijdelijke lijnen om snel een pickleballveld op te zetten.',
    whyStock: 'Sterke add-on bij netten en startersets met laag verzendrisico.',
    badges: ['Add-on', 'Veld opzetten', 'Lage prijs'],
    complianceNotes: ['Controleer vloercompatibiliteit en plakresten.', 'Waarschuw voor natte of ruwe ondergrond.']
  },
  {
    sku: 'PPB-GRIP-TAPE-3-001',
    slug: 'pickleball-overgrip-tape-3-pack',
    name: 'Pickleball Overgrip Tape 3-pack',
    category: 'tassen-accessoires',
    level: 'recreatief',
    brand: 'ProPickleball curated',
    supplierTarget: 'EU racket-sport accessoire-distributeur',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 9,
    marginTargetPct: 45,
    shortDescription: 'Overgrip voor meer controle, comfort en snelle paddle-refresh.',
    whyStock: 'Goedkope add-on met herhaalaankoop en lage retourkans.',
    badges: ['Grip', 'Tape', 'Add-on'],
    complianceNotes: ['Controleer materiaalclaims en allergiewaarschuwingen.', 'Geen comfortclaim zonder producttest.']
  },
  {
    sku: 'PPB-PADDLE-BAG-001',
    slug: 'pickleball-paddle-bag',
    name: 'Pickleball Paddle Bag',
    category: 'tassen-accessoires',
    level: 'recreatief',
    brand: 'ProPickleball curated',
    supplierTarget: 'EU sporttas-distributeur of merkdealer',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 34,
    marginTargetPct: 38,
    shortDescription: 'Tas voor paddles, ballen, grip tape en kleine accessoires.',
    whyStock: 'Logische bundel bij startersets en premium paddles.',
    badges: ['Tas', 'Bundel', 'Accessoire'],
    complianceNotes: ['Controleer afmetingen en materiaal.', 'Geen waterdicht-claim zonder leverancierbewijs.']
  },
  {
    sku: 'PPB-ONIX-EYE-OWL-001',
    slug: 'onix-owl-beschermbril',
    name: 'Onix Owl Beschermbril',
    category: 'schoenen-en-bescherming',
    level: 'recreatief',
    brand: 'Onix',
    supplierTarget: 'Onix / EU-distributeur',
    supplierStatus: 'verified_dealer_needed',
    supplierPathDocumented: false,
    commercialStatus: 'supplier_verification',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 24,
    marginTargetPct: 35,
    shortDescription: 'Oogbescherming als veiligheidsgerichte add-on voor jeugd, beginners en dubbelspel.',
    whyStock: 'Veiligheidspositie onderscheidt de shop, maar normclaims moeten eerst worden geverifieerd.',
    badges: ['Veiligheid', 'Add-on', 'Jeugd'],
    complianceNotes: ['Controleer exacte impactnorm per model.', 'Medische of blessurepreventiegaranties vermijden.']
  },
  {
    sku: 'PPB-COURT-SHOE-GUIDE-001',
    slug: 'court-shoe-keuzehulp',
    name: 'Court Shoe Keuzehulp',
    category: 'schoenen-en-bescherming',
    level: 'recreatief',
    brand: 'Wilson / Babolat / ASICS shortlist',
    supplierTarget: 'Affiliate of dealerafspraak met court-shoe leverancier',
    supplierStatus: 'fallback_retail_source',
    supplierPathDocumented: false,
    commercialStatus: 'lead_capture',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 119,
    marginTargetPct: 12,
    shortDescription: 'Keuzehulp voor court shoes met zijwaartse steun voor indoor en outdoor pickleball.',
    whyStock: 'Schoenen zijn belangrijk voor grip en zijwaartse beweging; de keuzehulp voorkomt dat je alleen op looks kiest.',
    badges: ['Keuzehulp', 'Schoenen later', 'Affiliate eerst'],
    complianceNotes: ['Geen medische garantieclaims.', 'Retourbeleid schoenen scherp regelen voor eigen voorraad.']
  },
  {
    sku: 'PPB-CLUBPACK-10-001',
    slug: 'propickleball-clubpack-10-spelers',
    name: 'ProPickleball Clubpack 10 spelers',
    category: 'clubpakketten',
    level: 'club',
    brand: 'ProPickleball curated',
    supplierTarget: 'Gemengde inkoop: paddles, ballen, netten, belijning, grips en tassen',
    supplierStatus: 'distribution_to_verify',
    supplierPathDocumented: false,
    commercialStatus: 'quote_request',
    stockStatus: 'not_stocked_prelaunch',
    checkoutEnabled: false,
    priceInclVatTarget: 599,
    marginTargetPct: 32,
    shortDescription: 'Offertepakket voor sportclubs, scholen, bedrijven en vakantieparken.',
    whyStock: 'Handig voor clubs en scholen die in een keer materiaal voor meerdere spelers willen plannen.',
    badges: ['B2B', 'Club', 'Offerte'],
    complianceNotes: ['Offerteproces verplicht bij grotere aantallen.', 'Levertermijn afhankelijk van dealerstatus en voorraadroute.']
  }
];

export const categories: { key: ProductCategory; label: string; description: string }[] = [
  { key: 'startersets', label: 'Startersets', description: 'Sets voor thuis, school, clubintro en vakantiepark.' },
  { key: 'paddles', label: 'Paddles', description: 'Van eerste paddle tot serieuzere controle- of powerkeuze.' },
  { key: 'ballen', label: 'Ballen', description: 'Indoor en outdoor ballen met duidelijke keuzehulp.' },
  { key: 'netten-en-belijning', label: 'Netten & belijning', description: 'Alles om snel een veld op te zetten.' },
  { key: 'schoenen-en-bescherming', label: 'Schoenen & bescherming', description: 'Court shoes en bescherming met duidelijke keuzehulp.' },
  { key: 'tassen-accessoires', label: 'Tassen & accessoires', description: 'Grips, tape, tassen en onderhoud als add-ons.' },
  { key: 'clubpakketten', label: 'Clubpakketten', description: 'Quote-request pakketten voor clubs, scholen en bedrijven.' }
];
